/**
 * Slide Registry System
 *
 * Convention-based auto-registration for slide components.
 * Eliminates switch statements - components register themselves with decorators.
 */

import { ComponentType } from "react";
import slideTypeRegistry from "./slide-types.json";

type SlideRegistryStore = {
  components: Map<string, ComponentType<any>>;
  loaders: Map<string, Promise<void>>;
};

const globalStore = (() => {
  const globalScope = globalThis as typeof globalThis & {
    __SLIDE_REGISTRY__?: SlideRegistryStore;
  };
  if (!globalScope.__SLIDE_REGISTRY__) {
    globalScope.__SLIDE_REGISTRY__ = {
      components: new Map<string, ComponentType<any>>(),
      loaders: new Map<string, Promise<void>>(),
    };
  }
  return globalScope.__SLIDE_REGISTRY__;
})();

// Global registry (singleton across module instances)
const SLIDE_REGISTRY = globalStore.components;
const SLIDE_REGISTRY_LOADERS = globalStore.loaders;

const BASE_REGISTRY_KEY = "__base__";

/**
 * Register a slide component.
 *
 * Usage:
 *   export const CoverSlide = registerSlide('cover', function CoverSlide(props: CoverSlideProps) {
 *     // ... implementation
 *   });
 *
 * @param slideType - Unique identifier for slide type (kebab-case)
 * @param Component - React component to register
 */
export function registerSlide<T extends ComponentType<any>>(
  slideType: string,
  Component: T,
): T {
  if (SLIDE_REGISTRY.has(slideType)) {
    console.warn(
      `[SlideRegistry] Overwriting existing registration for: ${slideType}`,
    );
  }

  SLIDE_REGISTRY.set(slideType, Component);

  if (process.env.NODE_ENV === "development") {
    console.log(`[SlideRegistry] Registered: ${slideType}`);
  }

  return Component;
}

/**
 * Get slide component by type.
 * Returns null if not registered.
 */
export function getSlideComponent(
  slideType: string,
): ComponentType<any> | null {
  return SLIDE_REGISTRY.get(slideType) || null;
}

/**
 * Validate that a slide type is registered.
 * Logs warning in development if not found.
 */
export function validateSlideType(slideType: string): boolean {
  const exists = SLIDE_REGISTRY.has(slideType);

  if (!exists && process.env.NODE_ENV === "development") {
    console.warn(`[SlideRegistry] Unknown slide type: ${slideType}`);
    console.log(
      "[SlideRegistry] Available types:",
      Array.from(SLIDE_REGISTRY.keys()).sort(),
    );
  }

  return exists;
}

/**
 * List all registered slide types.
 * Useful for debugging and validation.
 */
export function listRegisteredSlides(): string[] {
  return Array.from(SLIDE_REGISTRY.keys()).sort();
}

export function listExpectedSlideTypes(): string[] {
  const registry =
    (slideTypeRegistry as { slideTypes?: Record<string, unknown> })
      ?.slideTypes || {};
  return Object.keys(registry).sort();
}

export function validateRegistryCoverage(): {
  missing: string[];
  extra: string[];
} {
  const expected = new Set(listExpectedSlideTypes());
  const registered = new Set(listRegisteredSlides());
  const missing = Array.from(expected).filter((type) => !registered.has(type));
  const extra = Array.from(registered).filter((type) => !expected.has(type));
  return { missing, extra };
}

/**
 * Get registry statistics.
 * Useful for development tools and monitoring.
 */
export function getRegistryStats(): {
  total: number;
  types: string[];
} {
  return {
    total: SLIDE_REGISTRY.size,
    types: listRegisteredSlides(),
  };
}

/**
 * Clear registry (for testing only).
 */
export function clearRegistry(): void {
  SLIDE_REGISTRY.clear();
  SLIDE_REGISTRY_LOADERS.clear();
}

/**
 * Register component without decorator (alternative API).
 * Useful when decorators aren't available.
 */
export function registerSlideComponent(
  slideType: string,
  Component: ComponentType<any>,
): void {
  SLIDE_REGISTRY.set(slideType, Component);
}

function ensureRegistryLoader(
  key: string,
  load: () => Promise<unknown>,
): Promise<void> {
  if (!SLIDE_REGISTRY_LOADERS.has(key)) {
    SLIDE_REGISTRY_LOADERS.set(
      key,
      load().then(() => undefined),
    );
  }

  return SLIDE_REGISTRY_LOADERS.get(key) || Promise.resolve();
}

export async function ensureSlideRegistryLoaded(): Promise<void> {
  await ensureRegistryLoader(BASE_REGISTRY_KEY, () => import("./index"));
}
